import * as React from "react";
import {
    IClientBooking,
    IClient,
    IClinician,
    DateFormats,
    IAppointmentChecks,
    BookingStatus,
    ClientStatus,
    RecurrenceRule,
    IIntakeSessionHistory,
    ISessionAttendee,
} from "intake_shared/src/types";
import {
    TUpdateClient,
    TUpdateClientByStatus,
    TSaveClient,
} from "../../../clientUiTypes";
import styles from "./AppointmentHeldDetails.module.scss";
import { useToasts } from "react-toast-notifications";
import { CacheContext } from "../../../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../../../utils/intakeEmailSignature";
import { AclContext } from "../../../../../../contexts/AclContext";
import moment from "../../../../../../utils/app_moment";
import {
    findFeeByPracticeId,
    findLabelByPracticeId,
    findPracticeIdByLabel,
} from "../../../../../../utils/utils";
import {
    fetchCliniciansByIds,
    removeIntakeSessionFromSupervisor,
} from "../../../../../../store/clinician";
import ServiceIcon from "../../../../icons/Service";
import ClockIcon from "../../../../icons/Clock";
import LocationIcon from "../../../../icons/Location";
import { InfoIcon } from "../../../../icons/Info";
import { getV2BadgePropsByPractice } from "../../../../../../constants";
import { Badge } from "../../../../bits/Badge/Badge";
import { getApplicableFee, isFeeDowngraded } from "intake_shared/src/fees";
import { serviceDeliveryOptionLabels } from "intake_shared/src/constants";
import { Button } from "../../../../bits/Button/Button";
import { FormCheckbox } from "../../../../bits/FormCheckbox/FormCheckbox";
import SendIcon from "../../../../icons/Send";
import CancelIcon from "../../../../icons/Cancel";
import store from "../../../../../../store";
import { hasFeeChanges } from "../../../../../../store/feeRevision";
import { isAmdRequired } from "../../../../../../utils/amd";
import {
    getAppointmentConsentForm,
    getAppointmentConsentFormType,
    getHeldAppointmentCheck,
    mergeAppointmentChecksPreservingConsentCcNotes,
} from "../../../../../../utils/consent_form";
import {
    canAddConfiguredOtherParentGuardian,
    reconcileOtherParentConsentAttendee,
    getOtherParentGuardianClientFieldChanges,
} from "../../../../../../utils/guardian";
import { isClientMinor } from "../../../../../../utils/age";
import {
    sendAppointmentConsentFormEmail,
    sendCollateralConsentFormEmail,
} from "../../../../../../utils/queue_email";
import { TextArea } from "../../../../bits/TextArea/TextArea";
import { Input } from "../../../../bits/Input/Input";
import EditIcon from "../../../../icons/Edit";
import { Dropdown } from "../../../../bits/Dropdown/Dropdown";
import ChevronDownIcon from "../../../../icons/ChevronDown";
import ChangeBookingDetails from "../../AppointmentConfirmed/ChangeBookingDetails/ChangeBookingDetails";
import { Dialog } from "../../../../bits/Dialog/Dialog";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import SelectIntakeSessionEvaluatorDialog from "../../SelectIntakeSessionEvaluatorDialog/SelectIntakeSessionEvaluatorDialog";
import {
    isEligibleForIntakeSession,
    loadSupervisorsWithIntake as loadSupervisorsWithIntakeUtil,
    safeToDate,
} from "../../../../../v2utils";
import { IIntakeAvailability } from "intake_shared/src/types";
import { Loader } from "../../../../bits/Loader/Loader";
import { arrayUnion } from "firebase/firestore";
import {
    buildEmptySessionAttendee,
    getAttendeeDob,
    getAttendeeFullName,
    getCollateralConsentAppointmentCheck,
    getConsentRelevantSessionAttendees,
    getSessionAttendeeMissingFields,
    getUpdatedAttendeeConsentCheck,
    isCollateralConsentService,
    isMinorDob,
    isSessionAttendeeSupportedService,
} from "../../../../../../utils/session_attendee_consent";
import {
    addSessionAttendeesHistoryEntry,
    updateClientData,
} from "../../../../../../store/client";
import { DatePicker } from "../../../../bits/DatePicker/DatePicker";
import MailIcon from "../../../../icons/Mail";
import Table from "../../../../bits/Table/Table";

type ConsentRow = {
    id: string;
    type: "client" | "attendee";
    name: string;
    email: string;
    relationship: string;
    goodFormOnFile: boolean;
    sentAt: Date | null;
    reminderCount: number;
    reminderSentAt: Date | null;
    isMinor: boolean;
    dob: string | null;
    missingFields: string[];
    attendeeData: ISessionAttendee | undefined;
};

interface AppointmentHeldDetailsProps {
    appointment: IClientBooking;
    client: IClient;
    updateClient: TUpdateClient;
    updateClientByStatus: TUpdateClientByStatus;
    onSaveClient: TSaveClient;
}

export default function AppointmentHeldDetails({
    appointment,
    client,
    updateClient,
    updateClientByStatus,
    onSaveClient,
}: AppointmentHeldDetailsProps) {
    const { addToast } = useToasts();
    const olc = React.useContext(CacheContext);
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;
    const [showCancelDialog, setShowCancelDialog] = React.useState(false);
    const appointmentChecksRef = React.useRef(client.appointment_checks);
    appointmentChecksRef.current = client.appointment_checks;
    const appointmentCheck = (client.appointment_checks || {})[appointment.id];
    const appointmentConsentCcNotes = appointmentCheck?.consent_cc_notes;
    const consentCcNotes = appointmentConsentCcNotes ?? "";
    const [clinicianDetail, setClinicianDetail] = React.useState<IClinician[]>([]);
    const consentFormSent = appointmentCheck?.consent_form_sent || null;
    const consentFormRechecked = appointmentCheck?.consent_form_rechecked || null;
    const consentFormReminderSent =
        appointmentCheck?.consent_form_reminder_sent || null;
    const consentFormReminderCount =
        appointmentCheck?.consent_form_reminder_count ?? 0;
    const supervisorIntakeNotRequired =
        appointmentCheck?.supervisor_intake_not_required || null;
    const supervisorIntakeOveridden =
        appointmentCheck?.supervisor_intake_not_required_reason;
    const isSupervisorIntakeNotRequired = Boolean(supervisorIntakeNotRequired);
    const allDepartmentsAndServices = olc?.allDepartmentAndServices || {};
    const allConsentForms = olc?.consentForms || {};
    const allEmailSignatures = olc?.emailSignatures || {};
    const clientAddedToAmd = appointmentCheck?.client_added_to_amd || null;
    const [consentFormLoading, setConsentFormLoading] = React.useState(false);
    const [goodFormOnFileLoading, setGoodFormOnFileLoading] = React.useState(false);
    const [attendeeSaveLoading, setAttendeeSaveLoading] = React.useState(false);
    const [editingAttendeeIds, setEditingAttendeeIds] = React.useState<Set<string>>(
        new Set()
    );
    const [draftSessionAttendees, setDraftSessionAttendees] = React.useState<
        ISessionAttendee[]
    >(appointment.session_attendees || []);
    const [sendingConsentAttendeeId, setSendingConsentAttendeeId] = React.useState<
        string | null
    >(null);
    const [selectedConsentRecipients, setSelectedConsentRecipients] = React.useState<
        Set<string>
    >(new Set());

    const [editMode, setEditMode] = React.useState<{ [id: string]: boolean }>({});
    const [editedNotes, setEditedNotes] = React.useState<{ [id: string]: string }>(
        {}
    );
    const [intakeActionReason, setIntakeActionReason] = React.useState("");
    const [showChangeBookingModal, setShowChangeBookingModal] =
        React.useState(false);
    const [showIntakeSessionDialog, setShowIntakeSessionDialog] =
        React.useState(false);
    const [supervisors, setSupervisors] = React.useState<IClinician[]>([]);
    const [intakeAvailabilityBySupervisor, setIntakeAvailabilityBySupervisor] =
        React.useState<{ [key: string]: IIntakeAvailability[] }>({});
    const [showIntakeSessionLoader, setShowIntakeSessionLoader] =
        React.useState(false);
    const [showConfirmLoader, setShowConfirmLoader] = React.useState(false);
    const [
        showSupervisorIntakeNotRequiredDialog,
        setShowSupervisorIntakeNotRequiredDialog,
    ] = React.useState(false);
    const [supervisorIntakeNotRequiredReason, setSupervisorIntakeNotRequiredReason] =
        React.useState("");

    const signatureEmail = client.assigned_to || client.secondary_assignee || "";
    const intakeSignature = getIntakeEmailSignature(olc);
    const confirmedApptSignature = allEmailSignatures[signatureEmail as string];

    let consultCallCompleted: string = Boolean(
        appointmentCheck?.consult_call_completed
    )
        ? "Yes"
        : "No";

    if (consultCallCompleted && appointmentCheck?.consult_call_completed_on) {
        consultCallCompleted = `${consultCallCompleted} (on ${moment(
            appointmentCheck?.consult_call_completed_on
        ).format(DateFormats.DATE_FORMAT)})`;
    }

    const locationAddress = olc?.location_address?.[appointment.location || ""];

    const appointmentPractice = findLabelByPracticeId(
        appointment?.practice_id ||
            (process.env.REACT_APP_MANTLE_PRACTICE_ID as string),
        allDepartmentsAndServices
    );

    const supervisorPractice = findLabelByPracticeId(
        appointment?.supervisor_intake_session?.practice_id || "",
        allDepartmentsAndServices
    );

    const shouldShowAMD = isAmdRequired(
        appointmentPractice,
        appointment.requested_service
    );

    const amdRequirementSatisfied = !shouldShowAMD || Boolean(clientAddedToAmd);

    const hasValidConsent =
        Boolean(consentFormSent) || Boolean(consentFormRechecked);

    const isMinorSession = isClientMinor(client.date_of_birth);
    const supportsSessionAttendees = isSessionAttendeeSupportedService(
        appointment.requested_service
    );

    React.useEffect(() => {
        setDraftSessionAttendees(
            reconcileOtherParentConsentAttendee(
                client,
                appointment.session_attendees || []
            )
        );
    }, [appointment.id, appointment.session_attendees, client]);

    const activeAttendees = React.useMemo(
        () =>
            getConsentRelevantSessionAttendees(
                draftSessionAttendees,
                appointment.requested_service
            ),
        [appointment.requested_service, draftSessionAttendees]
    );
    React.useEffect(() => {
        const activeAttendeeIds = new Set(
            activeAttendees.map((attendee) => attendee.id)
        );
        setSelectedConsentRecipients((previous) => {
            const next = new Set(
                Array.from(previous).filter(
                    (recipientId) =>
                        recipientId === "client" ||
                        activeAttendeeIds.has(recipientId)
                )
            );
            return next.size === previous.size ? previous : next;
        });
    }, [activeAttendees]);
    const requiresCollateralConsent = isCollateralConsentService({
        ...appointment,
        session_attendees: activeAttendees,
    });
    const canAddConfiguredGuardian = canAddConfiguredOtherParentGuardian(
        client,
        activeAttendees
    );
    const collateralConsentMissingCount = requiresCollateralConsent
        ? activeAttendees.filter(
              (attendee) =>
                  getSessionAttendeeMissingFields(
                      attendee,
                      appointment.requested_service
                  ).length > 0 || !attendee.consent_check?.good_form_on_file
          ).length
        : 0;

    const totalConsentMissingCount =
        (Boolean(consentFormRechecked) ? 0 : 1) + collateralConsentMissingCount;

    const missingAppointmentFields = [
        !appointment.requested_service?.trim() ? "service type" : null,
        !appointment.service_delivery_option ? "method of delivery" : null,
    ].filter(Boolean) as string[];

    const canConfirmAppt =
        hasValidConsent &&
        amdRequirementSatisfied &&
        (!requiresCollateralConsent || collateralConsentMissingCount === 0);

    const handleConsentCcNotesChange = (value: string) => {
        const existingAppointmentCheck = client.appointment_checks?.[appointment.id];
        const updatedAppointmentChecks: IAppointmentChecks = {
            ...(client.appointment_checks || {}),
            [appointment.id]: {
                date_added: new Date(),
                client_requested_call: false,
                consult_call_notes: "",
                ...existingAppointmentCheck,
                consent_cc_notes: value,
            },
        };

        appointmentChecksRef.current = updatedAppointmentChecks;
        updateClient("appointment_checks", updatedAppointmentChecks);
    };

    const getUpdatedClientWithHeldBooking = (
        updatedBooking: IClientBooking,
        updatedAppointmentChecks: IAppointmentChecks
    ): IClient => ({
        ...client,
        held_bookings: (client.held_bookings || []).map((booking) =>
            booking.id === appointment.id ? updatedBooking : booking
        ),
        appointment_checks: updatedAppointmentChecks,
    });

    const persistSessionAttendees = async (
        updatedAttendees: ISessionAttendee[],
        extraAppointmentCheckFields: Partial<IAppointmentChecks[string]> = {},
        baseAppointmentChecks?: IAppointmentChecks
    ): Promise<ISessionAttendee[]> => {
        const filteredAttendees = updatedAttendees.filter((attendee) => {
            const isEmpty =
                editingAttendeeIds.has(attendee.id) &&
                !attendee.first_name.trim() &&
                !attendee.last_name.trim() &&
                !attendee.email?.trim();
            return !isEmpty;
        });
        const normalizedAttendees = filteredAttendees.map((attendee) => {
            const normalizedAttendee = {
                ...attendee,
                first_name: attendee.first_name.trim(),
                last_name: attendee.last_name.trim(),
                email: attendee.email?.trim() || "",
            };
            return {
                ...normalizedAttendee,
                consent_check: getUpdatedAttendeeConsentCheck(normalizedAttendee),
            };
        });
        const updatedBooking = {
            ...appointment,
            session_attendees: normalizedAttendees,
        };
        const resolvedAppointmentChecks =
            baseAppointmentChecks ?? client.appointment_checks;
        const updatedAppointmentCheck = {
            ...getCollateralConsentAppointmentCheck(
                resolvedAppointmentChecks?.[appointment.id],
                updatedBooking,
                normalizedAttendees
            ),
            ...extraAppointmentCheckFields,
            session_attendees_last_updated_at: new Date(),
            session_attendees_last_updated_by: loggedInUserEmail || null,
        };
        const updatedAppointmentChecks = {
            ...(resolvedAppointmentChecks || {}),
            [appointment.id]: updatedAppointmentCheck,
        };

        setAttendeeSaveLoading(true);
        try {
            const saved = await store.updateBooking(
                appointment.clinician_id,
                appointment.client_id,
                appointment.id,
                { session_attendees: normalizedAttendees },
                updatedAppointmentCheck,
                "held",
                loggedInUserEmail!
            );
            if (!saved) {
                throw new Error("Booking update failed");
            }
            const appointmentChecksToApply =
                mergeAppointmentChecksPreservingConsentCcNotes(
                    updatedAppointmentChecks,
                    appointmentChecksRef.current
                );
            appointmentChecksRef.current = appointmentChecksToApply;
            const updatedClient = getUpdatedClientWithHeldBooking(
                updatedBooking,
                appointmentChecksToApply
            );
            const guardianChanges = isMinorSession
                ? getOtherParentGuardianClientFieldChanges(
                      client,
                      normalizedAttendees
                  )
                : {};
            const updatedClientWithGuardianChanges = {
                ...updatedClient,
                ...guardianChanges,
            };
            updateClient("held_bookings", updatedClient.held_bookings);
            updateClient("appointment_checks", appointmentChecksToApply);
            setDraftSessionAttendees(normalizedAttendees);
            setEditingAttendeeIds(new Set());

            if (Object.keys(guardianChanges).length > 0) {
                try {
                    await updateClientData({
                        client_id: client.id,
                        changes: guardianChanges,
                        last_updated_by: loggedInUserEmail!,
                    });
                } catch (error) {
                    console.error(
                        "Failed to sync guardian client fields",
                        error instanceof Error ? error.message : String(error)
                    );
                    addToast(
                        "Attendee details saved, but guardian fields could not be synced to the client profile.",
                        { appearance: "warning" }
                    );
                }
            }

            try {
                await addSessionAttendeesHistoryEntry(client.id, {
                    booking_id: appointment.id,
                    saved_at: new Date(),
                    saved_by: loggedInUserEmail ?? "",
                    booking_status: "held",
                    booking_requested_service: appointment.requested_service ?? "",
                    attendees: normalizedAttendees,
                });
            } catch (error) {
                console.error(
                    "Failed to save session attendees history",
                    error instanceof Error ? error.message : String(error)
                );
            }

            onSaveClient(updatedClientWithGuardianChanges);
            return normalizedAttendees;
        } finally {
            setAttendeeSaveLoading(false);
        }
    };

    const saveSessionAttendees = async (updatedAttendees: ISessionAttendee[]) => {
        try {
            await persistSessionAttendees(updatedAttendees);
            addToast("Session attendee consent details saved", {
                appearance: "success",
            });
        } catch (error) {
            console.error("Failed to save session attendees", error);
            addToast("Failed to save session attendees", { appearance: "error" });
        }
    };

    const handleAddSessionAttendee = () => {
        const newAttendee = buildEmptySessionAttendee(
            canAddConfiguredGuardian
                ? {
                      first_name: client.other_parent?.trim() || "",
                      last_name: client.other_parent_last_name?.trim() || "",
                      email: client.other_parent_email?.trim() || "",
                      relationship: "Parent/Guardian",
                      guardian_role: "other",
                  }
                : {}
        );
        setDraftSessionAttendees((prev) => [...prev, newAttendee]);
        setEditingAttendeeIds(
            (prev) => new Set(Array.from(prev).concat(newAttendee.id))
        );
    };

    const handleCancelAttendeeEdit = () => {
        const savedIds = new Set(
            (appointment.session_attendees || []).map((a) => a.id)
        );
        setDraftSessionAttendees((prev) =>
            prev.filter((a) => !editingAttendeeIds.has(a.id) || savedIds.has(a.id))
        );
        setEditingAttendeeIds(new Set());
    };

    const handleSessionAttendeeChange = (
        attendeeId: string,
        changes: Partial<ISessionAttendee>
    ) => {
        const updatedAttendees = draftSessionAttendees.map((attendee) => {
            if (attendee.id !== attendeeId) return attendee;
            const updatedAttendee = {
                ...attendee,
                ...changes,
                updated_at: new Date(),
            };
            return {
                ...updatedAttendee,
                consent_check: getUpdatedAttendeeConsentCheck(updatedAttendee),
            };
        });
        setDraftSessionAttendees(updatedAttendees);
    };

    const handleSessionAttendeeConsentChange = async (attendeeId: string) => {
        const now = new Date();
        const updatedAttendees = draftSessionAttendees.map((attendee) => {
            if (attendee.id !== attendeeId) return attendee;
            const isGoodFormOnFile = Boolean(
                attendee.consent_check?.good_form_on_file
            );
            return {
                ...attendee,
                updated_at: now,
                consent_check: getUpdatedAttendeeConsentCheck(attendee, {
                    good_form_on_file: !isGoodFormOnFile,
                    rechecked_at: !isGoodFormOnFile ? now : null,
                    rechecked_by: !isGoodFormOnFile
                        ? loggedInUserEmail || null
                        : null,
                }),
            };
        });
        await saveSessionAttendees(updatedAttendees);
    };

    const handleRemoveSessionAttendee = async (attendeeId: string) => {
        const previousAttendees = draftSessionAttendees;
        const updatedAttendees = draftSessionAttendees.filter(
            (attendee) => attendee.id !== attendeeId
        );
        setDraftSessionAttendees(updatedAttendees);
        try {
            await persistSessionAttendees(updatedAttendees);
        } catch (error) {
            console.error("Failed to remove session attendee", error);
            setDraftSessionAttendees(previousAttendees);
            addToast("Failed to remove attendee. Please try again.", {
                appearance: "error",
            });
        }
    };

    const handleSendAttendeeConsentForm = async (
        attendee: ISessionAttendee,
        isReminder = false,
        baseAttendees?: ISessionAttendee[],
        baseAppointmentChecks?: IAppointmentChecks
    ): Promise<ISessionAttendee[] | undefined> => {
        if (!attendee.email?.trim()) return;
        const consentForm = getAppointmentConsentForm(
            appointmentPractice || "",
            appointment,
            allConsentForms
        );
        const isCustodian = isMinorSession && attendee.guardian_role === "other";
        setSendingConsentAttendeeId(attendee.id);
        try {
            await sendCollateralConsentFormEmail({
                client,
                appointment,
                attendeeId: attendee.id,
                attendeeFirstName:
                    attendee.first_name?.trim() ||
                    getAttendeeFullName(attendee) ||
                    "",
                attendeeEmail: attendee.email?.trim() || "",
                attendeeRelationship: attendee.relationship,
                attendeeIsMinor: isMinorDob(getAttendeeDob(attendee)),
                consentForm,
                signature: confirmedApptSignature,
                department: appointmentPractice || undefined,
                isCustodian,
                isReminder,
            });
            const sentAt = new Date();
            const dob = getAttendeeDob(attendee);
            const isMinor = isMinorDob(dob);
            const variant = isCustodian
                ? consentForm?.types?.find(
                      (type) =>
                          type.name === "Minors - Secondary Custodian" ||
                          type.name === "Minor - Secondary Custodian"
                  )
                : consentForm?.types?.find((type) =>
                      isMinor
                          ? type.name === "Collateral Consent MINOR"
                          : type.name === "Collateral Consent"
                  ) || consentForm?.types?.[0];
            const source = baseAttendees ?? draftSessionAttendees;
            const updatedAttendees = source.map((sessionAttendee) =>
                sessionAttendee.id === attendee.id
                    ? {
                          ...sessionAttendee,
                          updated_at: sentAt,
                          consent_check: getUpdatedAttendeeConsentCheck(
                              sessionAttendee,
                              isReminder
                                  ? {
                                        reminder_sent_at: sentAt,
                                        reminder_count:
                                            (sessionAttendee.consent_check
                                                ?.reminder_count ?? 0) + 1,
                                    }
                                  : {
                                        good_form_on_file: Boolean(
                                            sessionAttendee.consent_check
                                                ?.good_form_on_file
                                        ),
                                        collateral_consent_form_id:
                                            consentForm?.id || null,
                                        collateral_consent_form_name:
                                            consentForm?.name || null,
                                        collateral_consent_form_variant_id:
                                            variant?.id || null,
                                        collateral_consent_form_variant_name:
                                            variant?.name || null,
                                        sent_at: sentAt,
                                    }
                          ),
                      }
                    : sessionAttendee
            );
            let savedAttendees: ISessionAttendee[] | undefined;
            try {
                savedAttendees = await persistSessionAttendees(
                    updatedAttendees,
                    !isReminder &&
                        !(baseAppointmentChecks ?? client.appointment_checks ?? {})[
                            appointment.id
                        ]?.consent_form_sent
                        ? { consent_form_sent: sentAt }
                        : {},
                    baseAppointmentChecks
                );
            } catch (saveError) {
                console.error(
                    "Consent form sent but failed to save record",
                    saveError
                );
                addToast(
                    "Consent form was sent, but we could not save the record. Please refresh before retrying to avoid sending it again.",
                    { appearance: "error" }
                );
                return;
            }
            addToast(
                isReminder
                    ? `Consent reminder sent to ${attendee.email}`
                    : `Consent form sent to ${attendee.email}`,
                { appearance: "success" }
            );
            return savedAttendees;
        } catch (error) {
            console.error("Failed to send attendee consent form", error);
            addToast(
                isReminder
                    ? "Failed to send consent reminder"
                    : "Failed to send consent form",
                { appearance: "error" }
            );
        } finally {
            setSendingConsentAttendeeId(null);
        }
    };

    const handleSendConsentForm = async (
        isUrgentReminder: boolean
    ): Promise<IAppointmentChecks | undefined> => {
        setConsentFormLoading(true);
        try {
            const existingAppointmentCheck = getHeldAppointmentCheck(
                client.appointment_checks?.[appointment.id]
            );

            const resolvedForm = getAppointmentConsentForm(
                appointmentPractice || "",
                appointment,
                allConsentForms
            );
            const consentFormType = getAppointmentConsentFormType(
                appointmentPractice || "",
                appointment
            );
            if (!resolvedForm?.id) {
                throw new Error(
                    "No consent form is configured for this appointment."
                );
            }

            const sentAt = new Date();
            const resolvedClinician =
                clinicianDetail[0] ??
                (appointment?.clinician_id
                    ? (await fetchCliniciansByIds([appointment.clinician_id]))[0]
                    : undefined);
            const form = await sendAppointmentConsentFormEmail(
                client,
                appointment,
                appointmentPractice || "",
                allConsentForms,
                confirmedApptSignature,
                {
                    isUrgentReminder,
                    clinicianCredential: resolvedClinician?.credential,
                    queueEmail: (email, status) =>
                        isUrgentReminder
                            ? store.queueConsentReminderEmail(
                                  email,
                                  status,
                                  client.id,
                                  appointment.id,
                                  resolvedForm,
                                  consentFormType,
                                  loggedInUserEmail!
                              )
                            : store.queueConsentEmail(
                                  email,
                                  status,
                                  client.id,
                                  appointment.id,
                                  resolvedForm,
                                  consentFormType,
                                  loggedInUserEmail!
                              ),
                }
            );
            if (!form) {
                throw new Error(
                    "No consent form is configured for this appointment."
                );
            }

            const updatedAppointmentCheck: IAppointmentChecks[string] = {
                ...existingAppointmentCheck,
                consent_form: form,
                ...(consentFormType ? { consent_form_type: consentFormType } : {}),
            };

            if (isUrgentReminder) {
                updatedAppointmentCheck.consent_form_reminder_sent = sentAt;
                updatedAppointmentCheck.consent_form_reminder_count =
                    (existingAppointmentCheck.consent_form_reminder_count ?? 0) + 1;
            } else {
                updatedAppointmentCheck.consent_form_sent = sentAt;
            }

            const refreshedAppointmentChecks: IAppointmentChecks = {
                ...(client.appointment_checks || {}),
                [appointment.id]: updatedAppointmentCheck,
            };
            const updatedAppointmentChecks =
                mergeAppointmentChecksPreservingConsentCcNotes(
                    refreshedAppointmentChecks,
                    appointmentChecksRef.current
                );
            appointmentChecksRef.current = updatedAppointmentChecks;
            updateClient("appointment_checks", updatedAppointmentChecks);
            addToast(
                isUrgentReminder
                    ? "Consent Reminder Sent Successfully"
                    : "Consent Form Sent Successfully",
                {
                    appearance: "success",
                }
            );
            return updatedAppointmentChecks;
        } catch (error) {
            addToast(
                error instanceof Error
                    ? error.message
                    : isUrgentReminder
                    ? "Failed to send consent reminder."
                    : "Failed to send consent form.",
                {
                    appearance: "error",
                }
            );
        } finally {
            setConsentFormLoading(false);
        }
    };

    const handleGoodFormOnFileChange = async () => {
        const existingAppointmentCheck = getHeldAppointmentCheck(
            client.appointment_checks?.[appointment.id]
        );

        const isChecked = Boolean(existingAppointmentCheck.consent_form_rechecked);
        const form = getAppointmentConsentForm(
            appointmentPractice || "",
            appointment,
            allConsentForms
        );

        setGoodFormOnFileLoading(true);
        try {
            const refreshedAppointmentChecks =
                await store.updateHeldAppointmentConsentCheck(
                    client.id,
                    appointment.id,
                    form,
                    !isChecked,
                    loggedInUserEmail!
                );
            const updatedAppointmentChecks =
                mergeAppointmentChecksPreservingConsentCcNotes(
                    refreshedAppointmentChecks,
                    appointmentChecksRef.current
                );
            appointmentChecksRef.current = updatedAppointmentChecks;
            updateClient("appointment_checks", updatedAppointmentChecks);
        } catch {
            addToast("Failed to update good form on file.", {
                appearance: "error",
            });
        } finally {
            setGoodFormOnFileLoading(false);
        }
    };

    const handleBulkConsentSend = async () => {
        const recipients = new Set(selectedConsentRecipients);
        if (recipients.size === 0) return;

        try {
            let freshAppointmentChecks: IAppointmentChecks | undefined;
            if (recipients.has("client") && !consentFormRechecked) {
                const shouldRemind =
                    Boolean(consentFormSent) && !consentFormRechecked;
                freshAppointmentChecks = await handleSendConsentForm(shouldRemind);
            }
            const attendeesWithEmail = activeAttendees.filter(
                (a) =>
                    recipients.has(a.id) &&
                    a.email?.trim() &&
                    getSessionAttendeeMissingFields(a, appointment.requested_service)
                        .length === 0
            );
            let current = [...draftSessionAttendees];
            for (const a of attendeesWithEmail) {
                const latest = current.find((x) => x.id === a.id);
                if (!latest) continue;
                const next = await handleSendAttendeeConsentForm(
                    latest,
                    Boolean(latest.consent_check?.sent_at),
                    current,
                    freshAppointmentChecks
                );
                if (next) current = next;
            }
        } finally {
            setSelectedConsentRecipients(new Set());
        }
    };

    const handleSupervisorIntakeNotRequiredSave = async () => {
        setShowConfirmLoader(true);
        try {
            const updatedAppointmentChecks: IAppointmentChecks =
                client.appointment_checks || {};
            const existingCheck = updatedAppointmentChecks[appointment.id] || {};
            const reason = supervisorIntakeNotRequiredReason.trim();

            updatedAppointmentChecks[appointment.id] = {
                ...existingCheck,
                supervisor_intake_not_required: true,
                supervisor_intake_not_required_reason: reason,
            };

            setSupervisorIntakeNotRequiredReason(reason);
            updateClient("appointment_checks", updatedAppointmentChecks);

            const clientUpdates: Partial<IClient> = {
                appointment_checks: updatedAppointmentChecks,
            };

            try {
                const updated = await store.updateBooking(
                    appointment.clinician_id,
                    appointment.client_id,
                    appointment.id,
                    {
                        supervisor_intake_session: null,
                    },
                    {},
                    "held",
                    loggedInUserEmail!
                );
                if (!updated) {
                    addToast("Failed to update booking. Please try again.", {
                        appearance: "error",
                    });
                    return;
                }
            } catch (error) {
                console.error(
                    "Failed to update booking with cleared supervisor intake session:",
                    error
                );
                addToast("Failed to update booking. Please try again.", {
                    appearance: "error",
                });
                return;
            }

            const updatedHeldBookings = [
                ...(client.held_bookings || []).filter(
                    (b) => b.id !== appointment.id
                ),
                {
                    ...appointment,
                    supervisor_intake_session: null,
                },
            ];
            updateClient("held_bookings", updatedHeldBookings);
            clientUpdates.held_bookings = updatedHeldBookings;

            if (appointment?.supervisor_intake_session) {
                const originalSupervisorIntakeSession =
                    appointment?.supervisor_intake_session;

                try {
                    await store.removeIntakeSessionFromSupervisor(
                        originalSupervisorIntakeSession.supervisor_id,
                        appointment.id,
                        appointment.client_id
                    );
                } catch (error) {
                    console.error(
                        "Failed to remove intake session from supervisor:",
                        error
                    );
                    try {
                        await store.updateBooking(
                            appointment.clinician_id,
                            appointment.client_id,
                            appointment.id,
                            {
                                supervisor_intake_session:
                                    originalSupervisorIntakeSession,
                            },
                            {},
                            "held",
                            loggedInUserEmail!
                        );
                    } catch (rollbackError) {
                        console.error(
                            "Failed to rollback booking after supervisor removal error:",
                            rollbackError
                        );
                    }

                    addToast(
                        "Failed to remove intake session from supervisor. Your previous supervisor intake was restored. Please try again or contact support.",
                        {
                            appearance: "error",
                        }
                    );
                    return;
                }
            }
            setShowSupervisorIntakeNotRequiredDialog(false);
            addToast("Supervisor intake not required saved successfully", {
                appearance: "success",
            });
        } catch (error) {
            console.error(
                "Unexpected error in supervisor intake not required dialog:",
                error
            );
            addToast(
                "An unexpected error occurred. Please try again or contact support.",
                {
                    appearance: "error",
                }
            );
        } finally {
            setShowConfirmLoader(false);
        }
    };

    const handleEditClick = (id: string, currentNotes: string) => {
        setEditMode((prev) => ({ ...prev, [id]: true }));
        setEditedNotes((prev) => ({ ...prev, [id]: currentNotes }));
    };

    const handleCancelEdit = (id: string) => {
        setEditMode((prev) => ({ ...prev, [id]: false }));
        setEditedNotes((prev) => ({ ...prev, [id]: "" }));
    };

    const handleInputChange = (id: string, value: string) => {
        setEditedNotes((prev) => ({ ...prev, [id]: value }));
    };

    const handleSaveClick = async (appointment: IClientBooking) => {
        const updated = await store.updateBooking(
            appointment.clinician_id,
            appointment.client_id,
            appointment.id,
            {
                email_notes: editedNotes[appointment.id],
            },
            {},
            "held",
            loggedInUserEmail!
        );
        if (updated) {
            const updatedClient = {
                ...client,
                held_bookings: client.held_bookings?.map(
                    (heldAppt: IClientBooking) =>
                        heldAppt.id === appointment.id
                            ? {
                                  ...heldAppt,
                                  email_notes: editedNotes[appointment.id],
                              }
                            : heldAppt
                ),
            };

            onSaveClient(updatedClient);

            setEditMode((prev) => ({ ...prev, [appointment.id]: false }));
            addToast("Email notes updated", {
                appearance: "success",
            });
        } else {
            addToast("Unable to update email notes", {
                appearance: "error",
            });
        }
    };

    const getClinicianCredential = async () => {
        const res = await fetchCliniciansByIds([appointment?.clinician_id]);
        setClinicianDetail(res);
    };

    const feeType =
        findFeeByPracticeId(appointment?.practice_id, allDepartmentsAndServices) ||
        "";

    React.useEffect(() => {
        getClinicianCredential();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Check if eligible for intake session
    const isEligible = React.useMemo(() => {
        if (clinicianDetail.length === 0) return false;
        return isEligibleForIntakeSession(client, clinicianDetail[0], appointment);
    }, [client, clinicianDetail, appointment]);
    const isSupervisorIntakePending =
        isEligible &&
        !appointment.supervisor_intake_session &&
        !isSupervisorIntakeNotRequired;

    // Load supervisors with real intake availability from Firestore
    const loadSupervisorsWithIntake = async () => {
        if (clinicianDetail.length === 0) return;

        try {
            const clinician = clinicianDetail[0];
            const { enrichedSupervisors, availability } =
                await loadSupervisorsWithIntakeUtil(
                    clinician,
                    allDepartmentsAndServices,
                    store.fetchAllSupervisors
                );

            setSupervisors(enrichedSupervisors);
            setIntakeAvailabilityBySupervisor(availability);
        } catch (error) {
            console.error(
                "Failed to load supervisors with intake availability",
                error
            );
        }
    };

    const getSupervisorIntakeSessionExpiryStatus = () => {
        const supervisorIntakeSessionStartTime = moment(
            appointment.supervisor_intake_session?.start_time
        );
        if (!supervisorIntakeSessionStartTime.isValid()) {
            return "invalid";
        }
        if (supervisorIntakeSessionStartTime.isBefore(moment())) {
            return "expired";
        }
        return "active";
    };

    const handleIntakeSessionSelect = async (
        supervisorId: string,
        intakeSession: IIntakeAvailability
    ) => {
        if (!appointment.clinician_id || !appointment.client_id) {
            addToast("Unable to save intake session (missing IDs)", {
                appearance: "error",
            });
            return;
        }

        setShowIntakeSessionLoader(true);

        try {
            // Find the selected supervisor to determine practice_id
            const selectedSupervisor = supervisors.find(
                (sup) => sup.email === supervisorId || sup.id === supervisorId
            ) as
                | (IClinician & {
                      supervisorRole?: "primary" | "secondary";
                      supervisorPracticeLabel?: string;
                  })
                | undefined;

            let practiceId: string | undefined;
            let clinicianDisplayName: string = "";

            if (selectedSupervisor) {
                clinicianDisplayName =
                    selectedSupervisor.fullname ||
                    `${selectedSupervisor.firstname || ""} ${
                        selectedSupervisor.lastname || ""
                    }`.trim() ||
                    "";

                if (selectedSupervisor.supervisorRole === "primary") {
                    const praticeLabel = clinicianDetail?.[0]?.departments?.[0];
                    const practiceIdFromLabel = findPracticeIdByLabel(
                        praticeLabel,
                        allDepartmentsAndServices
                    );
                    practiceId = practiceIdFromLabel || "";
                } else if (selectedSupervisor.supervisorRole === "secondary") {
                    const clinician = clinicianDetail[0];
                    const secondaryPair = (
                        clinician.secondary_supervisor_ids || []
                    ).find((p) => {
                        const supId = selectedSupervisor.id || "";
                        const supEmail = selectedSupervisor.email || "";
                        return (
                            p.supervisor_id === supId || p.supervisor_id === supEmail
                        );
                    });
                    if (secondaryPair) {
                        practiceId = secondaryPair.practice_id;
                    }
                } else {
                    const praticeLabel = selectedSupervisor.departments?.[0];
                    const practiceIdFromLabel = findPracticeIdByLabel(
                        praticeLabel,
                        allDepartmentsAndServices
                    );
                    practiceId = practiceIdFromLabel || "";
                }
            }

            const resolvedIntakePracticeId =
                intakeSession.practice_id ||
                appointment.practice_id ||
                practiceId ||
                "";

            const previousIntake = appointment.supervisor_intake_session;

            if (client && appointment && previousIntake) {
                const clientIntakeSessionHistoryEntry: IIntakeSessionHistory = {
                    ...previousIntake,
                    booking_id: appointment.id,
                    supervisor_id: previousIntake.supervisor_id,
                    start_time: previousIntake.start_time,
                    end_time: previousIntake.end_time,
                    status: previousIntake.status,
                    actioned_by: loggedInUserEmail || "",
                    action: "rescheduled",
                    actioned_on: new Date(),
                    reason: intakeActionReason || "",
                    practice_id: previousIntake.practice_id || "",
                    clinician_display_name:
                        previousIntake.clinician_display_name || "",
                    service_delivery_option:
                        previousIntake.service_delivery_option || "remote",
                    location: previousIntake.location || "Remote",
                    recurrence: previousIntake.recurrence || RecurrenceRule.ONE_OFF,
                    booking_requested_service: appointment.requested_service || "",
                    booking_clinician_display_name:
                        appointment.clinician_display_name || "",
                    booking_start: appointment.start || "",
                    booking_starting_date: appointment.starting_date || "",
                    booking_practice_id: appointment.practice_id || "",
                };

                try {
                    await store.updateClientData({
                        client_id: client.id,
                        changes: {
                            intake_session_history: arrayUnion(
                                clientIntakeSessionHistoryEntry
                            ),
                        },
                        last_updated_by: loggedInUserEmail || "",
                    });
                } catch (error) {
                    console.error(
                        "Failed to update client intake session history for reschedule",
                        error
                    );
                }
            }
            const intakeSessionData = {
                id: intakeSession.id,
                supervisor_id: supervisorId,
                service_delivery_option: intakeSession.service_delivery_option,
                start_time: intakeSession.start_time,
                end_time: intakeSession.end_time,
                recurrence: RecurrenceRule.ONE_OFF as RecurrenceRule.ONE_OFF,
                created_at: intakeSession.created_at || new Date(),
                location: intakeSession.location || "",
                office: intakeSession.office || "",
                status: "held" as const,
                completed_at: null,
                practice_id: resolvedIntakePracticeId,
                clinician_display_name: clinicianDisplayName,
                supervisees_display_name: appointment.clinician_display_name || "",
            };

            const success = await store.updateBooking(
                appointment.clinician_id,
                appointment.client_id,
                appointment.id,
                {
                    supervisor_intake_session: intakeSessionData,
                },
                {},
                "held",
                loggedInUserEmail!
            );

            if (!success) {
                addToast("Failed to save intake session", {
                    appearance: "error",
                });
                return;
            }

            try {
                // If updating an existing intake session, clear the old one and book the new one
                if (appointment.supervisor_intake_session) {
                    const oldSupervisorId =
                        appointment.supervisor_intake_session.supervisor_id;
                    await store.replaceIntakeSessionBooking(
                        oldSupervisorId,
                        appointment.id,
                        appointment.client_id,
                        supervisorId,
                        intakeSession.id,
                        appointment.client_display_name,
                        "held",
                        resolvedIntakePracticeId,
                        appointment.clinician_display_name
                    );
                } else {
                    // New intake session, just book it
                    await store.updateIntakeSessionBooking(
                        supervisorId,
                        intakeSession.id,
                        appointment.id,
                        appointment.client_id,
                        appointment.client_display_name,
                        "held",
                        resolvedIntakePracticeId,
                        appointment.clinician_display_name
                    );
                }
            } catch (error) {
                console.error(
                    "Failed to update intake session booking status",
                    error
                );
            }

            const updatedHeldBookings = (client.held_bookings || []).map((b) =>
                b.id === appointment.id
                    ? {
                          ...b,
                          supervisor_intake_session: intakeSessionData,
                      }
                    : b
            );

            const updatedClient = {
                ...client,
                held_bookings: updatedHeldBookings,
            };

            onSaveClient(updatedClient);

            addToast("Intake session saved", {
                appearance: "success",
            });

            setShowIntakeSessionDialog(false);
        } finally {
            setShowIntakeSessionLoader(false);
        }
    };

    const handleConfirmAppointment = async (
        confirmType: "client_and_clinician_confirm" | "clinician_confirm_only"
    ) => {
        const missingLocation = !appointment.location?.trim();
        const missingService = !appointment.requested_service?.trim();
        if (missingLocation || missingService) {
            const missing = [
                missingLocation ? "location" : null,
                missingService ? "requested service" : null,
            ].filter(Boolean) as string[];
            addToast(
                `Cannot confirm this appointment: add ${missing.join(
                    " and "
                )} first (use Update booking appointment or contact support if needed).`,
                { appearance: "error" }
            );
            return;
        }

        setShowConfirmLoader(true);
        try {
            if (confirmType === "client_and_clinician_confirm") {
                await store.saveClientData(
                    client,
                    loggedInUserEmail!,
                    false,
                    hasFeeChanges(client)
                );

                await store.confirmAppointment(
                    appointment.clinician_id,
                    appointment.client_id,
                    appointment.id,
                    loggedInUserEmail!,
                    allDepartmentsAndServices,
                    confirmedApptSignature,
                    "client_and_clinician_confirm",
                    appointmentPractice || "",
                    locationAddress || "",
                    allConsentForms,
                    appointmentPractice as string,
                    intakeSignature
                );
                addToast("Appointment confirmed", {
                    appearance: "success",
                });
                onSaveClient(client);
            } else if (confirmType === "clinician_confirm_only") {
                await store.saveClientData(
                    client,
                    loggedInUserEmail!,
                    false,
                    hasFeeChanges(client)
                );

                await store.confirmAppointment(
                    appointment.clinician_id,
                    appointment.client_id,
                    appointment.id,
                    loggedInUserEmail!,
                    allDepartmentsAndServices,
                    confirmedApptSignature,
                    "clinician_confirm_only",
                    appointmentPractice || "",
                    locationAddress || "",
                    allConsentForms,
                    appointmentPractice as string,
                    intakeSignature
                );
                addToast("Appointment confirmed", {
                    appearance: "success",
                });
                onSaveClient(client);
            }
        } catch (error) {
            console.error("Failed to confirm appointment", error);
            addToast("Failed to confirm appointment", {
                appearance: "error",
            });
        } finally {
            setShowConfirmLoader(false);
        }
    };

    const handleConfirmClick = (
        confirmType: "client_and_clinician_confirm" | "clinician_confirm_only"
    ) => {
        if (!canConfirmAppt) {
            addToast(
                "Cannot confirm: consent and AMD requirements must be met before confirming.",
                { appearance: "error" }
            );
            return;
        }

        // If this booking is eligible for a supervisor intake session but doesn't have one yet,
        // block confirmation and instruct the user to add an intake session first.
        const intakeSessionExpired =
            getSupervisorIntakeSessionExpiryStatus() === "expired";

        if (isSupervisorIntakePending) {
            addToast(
                "This client requires a supervisor intake session. Please click the Add intake session button to add one.",
                {
                    appearance: "error",
                }
            );
            return;
        }
        if (intakeSessionExpired) {
            addToast(
                "Supervisor intake session expired (date is passed), please choose a new session to proceed. You can do so by clicking 'Change Supervisor Intake Session'",
                {
                    appearance: "error",
                }
            );
            return;
        }

        handleConfirmAppointment(confirmType);
    };

    const applicableFee = getApplicableFee(
        client,
        clinicianDetail?.[0]?.credential || "",
        clinicianDetail?.[0]?.email || "",
        appointment.requested_service,
        feeType
    );
    const feeToUse = applicableFee?.fee_to_use?.replace("$", " ");
    const feeDowngraded = isFeeDowngraded(
        clinicianDetail?.[0]?.credential || "",
        applicableFee.fee_type
    );

    const consentTableData: ConsentRow[] = React.useMemo(
        () => [
            {
                id: "client",
                type: "client",
                name:
                    [client.first_name, client.last_name]
                        .filter(Boolean)
                        .join(" ") || "Client",
                email: client.email || "",
                relationship: "Primary client",
                goodFormOnFile: Boolean(consentFormRechecked),
                sentAt: safeToDate(consentFormSent) || null,
                reminderCount: consentFormReminderCount,
                reminderSentAt: consentFormReminderSent || null,
                isMinor: false,
                dob: null,
                missingFields: [],
                attendeeData: undefined,
            },
            ...(requiresCollateralConsent
                ? activeAttendees.map((attendee) => {
                      const dob = getAttendeeDob(attendee);
                      return {
                          id: attendee.id,
                          type: "attendee" as const,
                          name: getAttendeeFullName(attendee) || "—",
                          email: attendee.email || "",
                          relationship: attendee.relationship || "—",
                          goodFormOnFile: Boolean(
                              attendee.consent_check?.good_form_on_file
                          ),
                          sentAt:
                              safeToDate(attendee.consent_check?.sent_at) || null,
                          reminderCount: attendee.consent_check?.reminder_count || 0,
                          reminderSentAt:
                              attendee.consent_check?.reminder_sent_at || null,
                          isMinor: isMinorDob(dob),
                          dob: dob || null,
                          missingFields: getSessionAttendeeMissingFields(
                              attendee,
                              appointment.requested_service
                          ),
                          attendeeData: attendee,
                      };
                  })
                : []),
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            client,
            consentFormSent,
            consentFormRechecked,
            consentFormReminderCount,
            consentFormReminderSent,
            requiresCollateralConsent,
            activeAttendees,
            appointment.requested_service,
        ]
    );

    const consentColumns = [
        {
            Header: "",
            id: "select",
            accessor: (row: ConsentRow) => {
                const canSelect =
                    row.type === "client"
                        ? Boolean(client.email?.trim()) &&
                          !Boolean(consentFormRechecked)
                        : Boolean(row.email?.trim()) &&
                          row.missingFields.length === 0;
                return (
                    <input
                        type="checkbox"
                        className={styles.consentSelectCheckbox}
                        checked={selectedConsentRecipients.has(row.id)}
                        disabled={!canSelect}
                        onChange={(e) =>
                            setSelectedConsentRecipients((prev) => {
                                const next = new Set(prev);
                                if (e.target.checked) next.add(row.id);
                                else next.delete(row.id);
                                return next;
                            })
                        }
                    />
                );
            },
        },
        {
            Header: "Recipient",
            id: "recipient",
            accessor: (row: ConsentRow) => (
                <div className={styles.consentRecipientCell}>
                    <span className={styles.consentRecipientName}>
                        {row.name}
                        {row.isMinor && (
                            <span className={styles.attendeeMinorTag}> Minor</span>
                        )}
                    </span>
                    <span className={styles.consentRecipientEmail}>
                        {row.email || "No email on file"}
                    </span>
                    {row.dob && (
                        <span className={styles.consentRecipientEmail}>
                            DOB:{" "}
                            {moment(row.dob).format(
                                DateFormats.DAY_MONTH_YEAR_FORMAT
                            )}
                        </span>
                    )}
                </div>
            ),
        },
        {
            Header: "Relationship",
            id: "relationship",
            accessor: (row: ConsentRow) => (
                <span className={styles.consentCellText}>{row.relationship}</span>
            ),
        },
        {
            Header: "Good form on file?",
            id: "goodForm",
            accessor: (row: ConsentRow) => {
                if (row.type === "client") {
                    return (
                        <FormCheckbox
                            wrapperClass={styles.recheckedCheckboxWrapper}
                            label={
                                consentFormRechecked
                                    ? moment(consentFormRechecked).format(
                                          DateFormats.DATE_FORMAT
                                      )
                                    : "Mark on file"
                            }
                            labelClass={styles.recheckedCheckboxLabel}
                            checked={Boolean(consentFormRechecked)}
                            onChange={handleGoodFormOnFileChange}
                            disabled={goodFormOnFileLoading}
                        />
                    );
                }
                const attendee = row.attendeeData!;
                return (
                    <FormCheckbox
                        wrapperClass={styles.recheckedCheckboxWrapper}
                        label={
                            attendee.consent_check?.rechecked_at
                                ? moment(attendee.consent_check.rechecked_at).format(
                                      DateFormats.DATE_FORMAT
                                  )
                                : "Mark on file"
                        }
                        labelClass={styles.recheckedCheckboxLabel}
                        checked={Boolean(attendee.consent_check?.good_form_on_file)}
                        disabled={attendeeSaveLoading}
                        onChange={() =>
                            handleSessionAttendeeConsentChange(attendee.id)
                        }
                    />
                );
            },
        },
        {
            Header: "Status",
            id: "mantleStatus",
            accessor: (row: ConsentRow) => {
                const mantleStatus =
                    appointmentCheck?.mantle_consent_status ||
                    (consentFormSent || consentFormReminderSent ? "sent" : null);

                if (row.type !== "client" || !mantleStatus) {
                    return <span className={styles.consentCellText}>&mdash;</span>;
                }

                const needsCorrection = mantleStatus
                    .toLowerCase()
                    .includes("correction");
                const statusBadge = (
                    <Badge
                        content={mantleStatus.replace(/_/g, " ").toUpperCase()}
                        textColor={needsCorrection ? "#92400E" : "#205773"}
                        bgColor={needsCorrection ? "#FEF3C7" : "#C6ECFF"}
                    />
                );

                if (!needsCorrection || !appointmentCheck?.correction_reason) {
                    return statusBadge;
                }

                return (
                    <span className={styles.mantleStatusWithInfo}>
                        {statusBadge}
                        <OverlayTrigger
                            placement="top"
                            trigger={["hover", "focus"]}
                            overlay={
                                <Tooltip
                                    id={`mantle-consent-correction-${appointment.id}`}
                                >
                                    {appointmentCheck.correction_reason}
                                </Tooltip>
                            }
                        >
                            <span
                                className={styles.mantleStatusInfoIcon}
                                tabIndex={0}
                                aria-label="View Mantle consent correction reason"
                            >
                                <InfoIcon width="14" height="14" stroke="#92400E" />
                            </span>
                        </OverlayTrigger>
                    </span>
                );
            },
        },
        {
            Header: "Sent",
            id: "sent",
            accessor: (row: ConsentRow) => (
                <span className={styles.consentCellText}>
                    {row.sentAt
                        ? moment(row.sentAt).format(
                              DateFormats.DAY_YEAR_HOUR_MIN_FORMAT
                          )
                        : "—"}
                </span>
            ),
        },
        {
            Header: "Reminder / Actions",
            id: "actions",
            accessor: (row: ConsentRow) => {
                if (row.type === "client") {
                    return (
                        <div className={styles.consentActionCell}>
                            {row.reminderCount > 0 && row.reminderSentAt && (
                                <span className={styles.consentCellText}>
                                    {row.reminderCount}x · last{" "}
                                    {moment(row.reminderSentAt).format(
                                        DateFormats.DATE_FORMAT
                                    )}
                                </span>
                            )}
                            {!consentFormSent && (
                                <Button
                                    variant="secondary"
                                    iconLeft={<SendIcon />}
                                    onClick={() => handleSendConsentForm(false)}
                                    disabled={consentFormLoading}
                                >
                                    Send
                                </Button>
                            )}
                            {consentFormSent && !consentFormRechecked && (
                                <Button
                                    variant="secondary"
                                    iconLeft={<SendIcon />}
                                    onClick={() => handleSendConsentForm(true)}
                                    disabled={consentFormLoading}
                                >
                                    Remind
                                </Button>
                            )}
                        </div>
                    );
                }
                const attendee = row.attendeeData!;
                const canSend =
                    Boolean(attendee.email?.trim()) &&
                    row.missingFields.length === 0;
                return (
                    <div className={styles.consentActionCell}>
                        {(attendee.consent_check?.reminder_count ?? 0) > 0 && (
                            <span className={styles.consentCellText}>
                                {attendee.consent_check?.reminder_count}x · last{" "}
                                {moment(
                                    attendee.consent_check?.reminder_sent_at
                                ).format(DateFormats.DATE_FORMAT)}
                            </span>
                        )}
                        {canSend && (
                            <Button
                                variant="secondary"
                                iconLeft={<SendIcon />}
                                disabled={attendeeSaveLoading}
                                onClick={() =>
                                    handleSendAttendeeConsentForm(
                                        attendee,
                                        Boolean(attendee.consent_check?.sent_at)
                                    )
                                }
                            >
                                {sendingConsentAttendeeId === attendee.id
                                    ? "Sending…"
                                    : attendee.consent_check?.sent_at
                                    ? "Remind"
                                    : "Send"}
                            </Button>
                        )}
                        {row.missingFields.length > 0 && (
                            <span className={styles.attendeeMissingWarning}>
                                Missing:{" "}
                                {row.missingFields
                                    .map((f) => f.replace(/_/g, " "))
                                    .join(", ")}
                            </span>
                        )}
                        <div className={styles.attendeeInfoActions}>
                            <Button
                                variant="normal"
                                size="icon"
                                title="Edit attendee"
                                aria-label="Edit attendee"
                                disabled={attendeeSaveLoading}
                                onClick={() =>
                                    setEditingAttendeeIds(
                                        (prev) =>
                                            new Set(
                                                Array.from(prev).concat(attendee.id)
                                            )
                                    )
                                }
                            >
                                <EditIcon />
                            </Button>
                            <Button
                                variant="normal"
                                size="icon"
                                title="Remove attendee"
                                aria-label="Remove attendee"
                                disabled={attendeeSaveLoading}
                                onClick={() =>
                                    handleRemoveSessionAttendee(attendee.id)
                                }
                            >
                                <CancelIcon stroke="var(--color-danger)" />
                            </Button>
                        </div>
                    </div>
                );
            },
        },
    ];

    const editingAttendeeForDialog =
        editingAttendeeIds.size > 0
            ? draftSessionAttendees.find(
                  (a) => a.id === Array.from(editingAttendeeIds)[0]
              ) ?? null
            : null;

    return (
        <>
            <Loader show={showIntakeSessionLoader || showConfirmLoader} />
            {showChangeBookingModal && (
                <ChangeBookingDetails
                    client={client}
                    updateClient={updateClient}
                    booking={appointment}
                    appointmentState="held"
                    viewFrom="appt_held"
                    clinician={clinicianDetail}
                    showEditModal={showChangeBookingModal}
                    setShowEditModal={setShowChangeBookingModal}
                />
            )}
            <Dialog
                open={showCancelDialog}
                onClose={() => setShowCancelDialog(false)}
                onCancel={() => setShowCancelDialog(false)}
                saveText="Cancel Appointment"
                cancelText="Cancel"
                title="Cancel Appointment"
                onSave={async () => {
                    setShowConfirmLoader(true);
                    await store.saveClientData(client, loggedInUserEmail!);
                    if (appointment?.supervisor_intake_session) {
                        await removeIntakeSessionFromSupervisor(
                            appointment?.supervisor_intake_session?.supervisor_id,
                            appointment.id,
                            appointment.client_id
                        );
                    }
                    await store.deleteAppoinment({
                        clinicianId: appointment.clinician_id,
                        clientId: appointment.client_id,
                        appointmentId: appointment.id,
                        cancelledBy: loggedInUserEmail || "Intake",
                        status: BookingStatus.HELD,
                        cancellationNotes:
                            client[ClientStatus.APPT_HELD]?.cancellation_notes,
                        lastUpdatedBy: loggedInUserEmail!,
                    });

                    onSaveClient(client);
                    setShowCancelDialog(false);
                    setShowConfirmLoader(false);
                }}
            >
                <TextArea
                    label="Cancellation notes"
                    value={client[ClientStatus.APPT_HELD]?.cancellation_notes}
                    onChange={(e) => {
                        updateClientByStatus(
                            ClientStatus.APPT_HELD,
                            "cancellation_notes",
                            e.target.value
                        );
                    }}
                />
            </Dialog>
            <Dialog
                open={showSupervisorIntakeNotRequiredDialog}
                onClose={() => setShowSupervisorIntakeNotRequiredDialog(false)}
                onCancel={() => setShowSupervisorIntakeNotRequiredDialog(false)}
                saveText="Save"
                cancelText="Cancel"
                title="Supervisor intake not required"
                isSubmitBtnDisabled={!supervisorIntakeNotRequiredReason.trim()}
                onSave={handleSupervisorIntakeNotRequiredSave}
            >
                <TextArea
                    label="Please explain why supervisor intake is not required for this booking"
                    value={supervisorIntakeNotRequiredReason}
                    onChange={(e) =>
                        setSupervisorIntakeNotRequiredReason(e.target.value)
                    }
                />
            </Dialog>
            <div className={styles.appointmentHeldDetails}>
                <div className={styles.appointmentHeader}>
                    <div className={styles.clinicianInfo}>
                        <div className={styles.intakeClinicianHeader}>
                            <h3 className={styles.clinicianName}>
                                {appointment.clinician_display_name}
                            </h3>
                        </div>
                        <div className={styles.clinicianBadges}>
                            <Badge
                                tone="info"
                                content={clinicianDetail?.[0]?.credential}
                            />
                            <Badge
                                content={appointmentPractice}
                                textColor={
                                    getV2BadgePropsByPractice(appointmentPractice)
                                        .textColor
                                }
                                bgColor={
                                    getV2BadgePropsByPractice(appointmentPractice)
                                        .bgColor
                                }
                            />
                        </div>
                    </div>
                    <div className={styles.confirmActions}>
                        {shouldShowAMD && (
                            <FormCheckbox
                                wrapperClass={styles.recheckedCheckboxWrapper}
                                name="clientAddedToAmd"
                                label={
                                    clientAddedToAmd
                                        ? `AMD (on ${moment(clientAddedToAmd).format(
                                              DateFormats.DATE_FORMAT
                                          )})`
                                        : "AMD"
                                }
                                labelClass={styles.recheckedCheckboxLabel}
                                checked={Boolean(clientAddedToAmd)}
                                onChange={() => {
                                    const updatedAppointmentChecks: IAppointmentChecks =
                                        client.appointment_checks || {};
                                    const isChecked = Boolean(
                                        (
                                            updatedAppointmentChecks[
                                                appointment.id
                                            ] || {}
                                        ).client_added_to_amd
                                    );
                                    updatedAppointmentChecks[appointment.id] = {
                                        ...(updatedAppointmentChecks[
                                            appointment.id
                                        ] || {}),
                                        client_added_to_amd: isChecked
                                            ? null
                                            : new Date(),
                                    };
                                    updateClient(
                                        "appointment_checks",
                                        updatedAppointmentChecks
                                    );
                                }}
                            />
                        )}

                        <Button
                            variant="secondary"
                            onClick={() => setShowChangeBookingModal(true)}
                            iconLeft={<EditIcon />}
                        >
                            Change Booking
                        </Button>
                        <Dropdown>
                            <Dropdown.Trigger>
                                <Button
                                    variant="primary"
                                    iconRight={
                                        <ChevronDownIcon stroke="var(--color-white)" />
                                    }
                                    disabled={!canConfirmAppt}
                                >
                                    Confirm
                                </Button>
                            </Dropdown.Trigger>
                            <Dropdown.Content width="280px" align="end">
                                <Dropdown.Item
                                    onClick={() =>
                                        handleConfirmClick(
                                            "client_and_clinician_confirm"
                                        )
                                    }
                                >
                                    <span className={styles.confirmOption}>
                                        <span>Confirm appointment</span>
                                        <span className={styles.confirmOptionHint}>
                                            Also sends the client their confirmation
                                            emails
                                        </span>
                                    </span>
                                </Dropdown.Item>

                                <Dropdown.Item
                                    onClick={() =>
                                        handleConfirmClick("clinician_confirm_only")
                                    }
                                >
                                    <span className={styles.confirmOption}>
                                        <span>Clinician confirm only</span>
                                        <span className={styles.confirmOptionHint}>
                                            Client confirmation emails are not sent
                                        </span>
                                    </span>
                                </Dropdown.Item>
                            </Dropdown.Content>
                        </Dropdown>
                        <Button
                            variant="danger"
                            mode="outline"
                            iconLeft={<CancelIcon stroke="currentColor" />}
                            onClick={() => setShowCancelDialog(true)}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>

                {missingAppointmentFields.length > 0 && (
                    <div className={styles.missingInfoAlert} role="alert">
                        <InfoIcon width="16" height="16" />
                        <span>
                            Missing appointment information:{" "}
                            {missingAppointmentFields.join(" and ")}. Use Change
                            Booking to add it.
                        </span>
                    </div>
                )}

                <div className={styles.detailsGrid}>
                    <div className={styles.detailCard}>
                        <div className={styles.detailIcon}>
                            <ServiceIcon />
                        </div>
                        <div className={styles.detailContent}>
                            <div className={styles.detailLabel}>Service</div>
                            <div className={styles.detailValue}>
                                {appointment.requested_service || "Missing"}
                            </div>
                            <div className={styles.detailSubtext}>
                                <div className={styles.feeMeta}>
                                    <span>
                                        {`(${applicableFee.fee_type} - ${
                                            feeToUse === "NOT ACCEPTED"
                                                ? feeToUse
                                                : `$${feeToUse}`
                                        })`}
                                    </span>
                                    {applicableFee.fee_source === "general" ? (
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip
                                                    id={`held-fee-source-${appointment.id}`}
                                                >
                                                    General Fee used
                                                </Tooltip>
                                            }
                                        >
                                            <span
                                                className={styles.feeInfoIcon}
                                                aria-label="General fee used"
                                                tabIndex={0}
                                            >
                                                <InfoIcon width="14" height="14" />
                                            </span>
                                        </OverlayTrigger>
                                    ) : null}
                                </div>
                                {client[ClientStatus.FEE_SETTING]?.is_self_pay ||
                                client.insurance_provider?.toLowerCase() ===
                                    "self-pay" ? (
                                    <div className={styles.feePaymentType}>
                                        Self Pay
                                    </div>
                                ) : null}
                            </div>

                            {feeDowngraded ? (
                                <div className={styles.feeWarningText}>
                                    {`⚠ ${clinicianDetail?.[0]?.credential} tier fee is not set. Using ${applicableFee.fee_type} instead. Please update the client's fees.`}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <div className={styles.detailCard}>
                        <div className={styles.detailIcon}>
                            <ClockIcon />
                        </div>
                        <div className={styles.detailContent}>
                            <div className={styles.detailLabel}>Time</div>
                            <div className={styles.detailValue}>
                                {moment(appointment.start).format(
                                    DateFormats.DAY_TIME_FORMAT
                                )}
                                {appointment.recurrenceRule && (
                                    <> ({appointment.recurrenceRule})</>
                                )}
                            </div>
                            <div
                                className={
                                    appointment.service_delivery_option
                                        ? styles.detailSubtext
                                        : styles.missingDetailValue
                                }
                            >
                                {appointment.service_delivery_option
                                    ? serviceDeliveryOptionLabels[
                                          appointment.service_delivery_option
                                      ]
                                    : "Method of delivery missing"}
                            </div>
                        </div>
                    </div>
                    <div className={styles.detailCard}>
                        <div className={styles.detailIcon}>
                            <LocationIcon />
                        </div>
                        <div className={styles.detailContent}>
                            <div className={styles.detailLabel}>Location</div>
                            <div className={styles.detailValue}>
                                {appointment.location}{" "}
                                {appointment.location !== "Remote" &&
                                    `(${appointment.office})`}
                            </div>
                            <div className={styles.detailSubtext}>
                                Starts{" "}
                                {moment(appointment.starting_date).format(
                                    DateFormats.DAY_MONTH_FORMAT
                                )}{" "}
                                at{" "}
                                {moment(appointment.starting_date).format(
                                    DateFormats.HOUR_MIN_FORMAT
                                )}
                            </div>
                        </div>
                    </div>
                    <div className={styles.detailCard}>
                        <div className={styles.detailIcon}>
                            <InfoIcon />
                        </div>
                        <div className={styles.detailContent}>
                            <div className={styles.detailLabel}>Status</div>
                            <div className={styles.detailSubtext}>
                                Consult call requested:{" "}
                                {Boolean(appointmentCheck?.client_requested_call)
                                    ? "Yes"
                                    : "No"}
                                {appointmentCheck?.date_added &&
                                    ` (${moment(appointmentCheck?.date_added).format(
                                        DateFormats.DATE_FORMAT
                                    )})`}
                            </div>
                            <div className={styles.detailSubtext}>
                                Consult call completed:{" "}
                                {consultCallCompleted?.startsWith("Yes")
                                    ? "Yes"
                                    : "No"}
                            </div>
                            {appointment.reservedFor && (
                                <div className={styles.detailSubtext}>
                                    Held from reserved time:{" "}
                                    {appointment.reservedFor && "Yes"}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {isEligible && (
                    <div className={styles.infoBox}>
                        <div className={styles.infoBoxTitle}>
                            Supervisor Intake
                            <span className={styles.sessionStatus}>
                                (
                                {appointment.supervisor_intake_session
                                    ? "Booked"
                                    : "Not Booked"}
                                )
                            </span>
                            {isSupervisorIntakePending && (
                                <Badge
                                    tone="warning"
                                    content="Clinician is eligible for supervisor intake session"
                                />
                            )}
                            {getSupervisorIntakeSessionExpiryStatus() ===
                                "expired" &&
                                !isSupervisorIntakeNotRequired && (
                                    <Badge
                                        tone="danger"
                                        content="Supervisor intake session expired (date is passed), please choose a new session"
                                    />
                                )}
                        </div>

                        <div className={styles.primaryActions}>
                            {appointment.supervisor_intake_session && (
                                <div className={styles.detailsTitle}>
                                    {
                                        appointment.supervisor_intake_session
                                            ?.clinician_display_name
                                    }{" "}
                                    ({supervisorPractice})
                                </div>
                            )}
                            {appointment.supervisor_intake_session && (
                                <div className={styles.detailSubtext}>
                                    <>
                                        {moment(
                                            appointment.supervisor_intake_session
                                                .start_time
                                        ).format("ddd MMM Do, h:mma")}{" "}
                                        -{" "}
                                        {moment(
                                            appointment.supervisor_intake_session
                                                .end_time
                                        ).format("h:mma")}
                                    </>
                                    (
                                    {appointment.supervisor_intake_session?.location}
                                    )
                                </div>
                            )}
                            {!isSupervisorIntakeNotRequired && (
                                <Button
                                    variant="secondary"
                                    size="small"
                                    className={styles.recheckedCheckboxLabel}
                                    onClick={async () => {
                                        await loadSupervisorsWithIntake();
                                        setShowIntakeSessionDialog(true);
                                    }}
                                >
                                    {appointment.supervisor_intake_session
                                        ? "Change Supervisor Intake Session"
                                        : "Add Supervisor intake session"}
                                </Button>
                            )}
                            <FormCheckbox
                                wrapperClass={
                                    styles.supervisorIntakeNotRequiredCheckboxWrapper
                                }
                                label="Supervisor intake not required for this booking"
                                labelClass={styles.recheckedCheckboxLabel}
                                checked={Boolean(supervisorIntakeNotRequired)}
                                onChange={async () => {
                                    const updatedAppointmentChecks: IAppointmentChecks =
                                        client.appointment_checks || {};
                                    const existingCheck =
                                        updatedAppointmentChecks[appointment.id] ||
                                        {};
                                    const isChecked = Boolean(
                                        existingCheck.supervisor_intake_not_required
                                    );

                                    if (!isChecked) {
                                        setSupervisorIntakeNotRequiredReason(
                                            existingCheck.supervisor_intake_not_required_reason ||
                                                ""
                                        );
                                        setShowSupervisorIntakeNotRequiredDialog(
                                            true
                                        );
                                        return;
                                    }

                                    updatedAppointmentChecks[appointment.id] = {
                                        ...existingCheck,
                                        supervisor_intake_not_required: false,
                                        supervisor_intake_not_required_reason: "",
                                    };
                                    updateClient(
                                        "appointment_checks",
                                        updatedAppointmentChecks
                                    );
                                }}
                            />

                            {supervisorIntakeOveridden && (
                                <div className={styles.infoBoxDetail}>
                                    Reason for skipping: {supervisorIntakeOveridden}
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <div className={styles.consentBox}>
                    <div className={styles.consentBoxTitle}>
                        Consent details
                        <div className={styles.consentBoxTitleActions}>
                            {totalConsentMissingCount > 0 && (
                                <Badge
                                    tone="danger"
                                    content={`Consent incomplete (${totalConsentMissingCount})`}
                                />
                            )}
                            {selectedConsentRecipients.size > 0 && (
                                <Button
                                    variant="primary"
                                    mode="outline"
                                    onClick={handleBulkConsentSend}
                                    iconLeft={<MailIcon />}
                                    disabled={
                                        consentFormLoading ||
                                        sendingConsentAttendeeId !== null
                                    }
                                >
                                    Send selected ({selectedConsentRecipients.size})
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className={styles.consentTableWrapper}>
                        <Table
                            columns={consentColumns}
                            data={consentTableData}
                            allowSelect={false}
                            variant="compact"
                        />
                    </div>
                    {editingAttendeeForDialog &&
                        (() => {
                            const attendee = editingAttendeeForDialog;
                            const missingFields = getSessionAttendeeMissingFields(
                                attendee,
                                appointment.requested_service
                            );
                            const dob = getAttendeeDob(attendee);
                            const dobDate = dob ? moment(dob).toDate() : null;
                            return (
                                <div className={styles.attendeeInlineForm}>
                                    <div
                                        className={[
                                            styles.attendeeFieldsGrid,
                                            appointment.requested_service ===
                                            "Family Therapy"
                                                ? styles.attendeeFieldsGridFive
                                                : "",
                                        ]
                                            .filter(Boolean)
                                            .join(" ")}
                                    >
                                        <Input
                                            label="First name"
                                            isRequired
                                            value={attendee.first_name}
                                            hasError={
                                                missingFields.includes("name") &&
                                                !attendee.first_name.trim()
                                            }
                                            errorText="Required"
                                            onChange={(e) =>
                                                handleSessionAttendeeChange(
                                                    attendee.id,
                                                    { first_name: e.target.value }
                                                )
                                            }
                                            inputSize="small"
                                        />
                                        <Input
                                            label="Last name"
                                            isRequired
                                            value={attendee.last_name}
                                            hasError={
                                                missingFields.includes("name") &&
                                                !attendee.last_name.trim()
                                            }
                                            errorText="Required"
                                            onChange={(e) =>
                                                handleSessionAttendeeChange(
                                                    attendee.id,
                                                    { last_name: e.target.value }
                                                )
                                            }
                                            inputSize="small"
                                        />
                                        <Input
                                            label="Relationship"
                                            value={attendee.relationship || ""}
                                            onChange={(e) =>
                                                handleSessionAttendeeChange(
                                                    attendee.id,
                                                    {
                                                        relationship: e.target.value,
                                                    }
                                                )
                                            }
                                            inputSize="small"
                                        />
                                        <Input
                                            type="email"
                                            label="Email"
                                            isRequired
                                            value={attendee.email || ""}
                                            hasError={missingFields.includes(
                                                "email"
                                            )}
                                            errorText="Required for adults"
                                            onChange={(e) =>
                                                handleSessionAttendeeChange(
                                                    attendee.id,
                                                    { email: e.target.value }
                                                )
                                            }
                                            inputSize="small"
                                        />
                                        {appointment.requested_service ===
                                            "Family Therapy" && (
                                            <DatePicker
                                                label="Date of birth"
                                                isRequired
                                                dateFormat="MM/dd/yyyy"
                                                selected={dobDate}
                                                hasError={missingFields.includes(
                                                    "date_of_birth"
                                                )}
                                                errorText="Required"
                                                placeholderText="Date of birth"
                                                onChange={(date) =>
                                                    handleSessionAttendeeChange(
                                                        attendee.id,
                                                        {
                                                            date_of_birth: date
                                                                ? moment(
                                                                      date as Date
                                                                  ).format(
                                                                      "YYYY-MM-DD"
                                                                  )
                                                                : null,
                                                        }
                                                    )
                                                }
                                                inputSize="small"
                                            />
                                        )}
                                    </div>
                                    <div className={styles.inlineFormActions}>
                                        <Button
                                            onClick={() =>
                                                saveSessionAttendees(
                                                    draftSessionAttendees
                                                )
                                            }
                                            disabled={attendeeSaveLoading}
                                        >
                                            {attendeeSaveLoading
                                                ? "Saving…"
                                                : "Save"}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={handleCancelAttendeeEdit}
                                            disabled={attendeeSaveLoading}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            );
                        })()}
                    {(supportsSessionAttendees || canAddConfiguredGuardian) &&
                        !editingAttendeeForDialog && (
                            <div className={styles.sessionAttendeesActions}>
                                <Button
                                    variant="secondary"
                                    disabled={attendeeSaveLoading}
                                    onClick={handleAddSessionAttendee}
                                >
                                    {canAddConfiguredGuardian
                                        ? "+ Add other parent or guardian"
                                        : "+ Add attendee"}
                                </Button>
                            </div>
                        )}
                </div>
                <div className={styles.notesWrapper}>
                    {appointmentCheck?.consult_call_notes && (
                        <div className={styles.emailNotesSection}>
                            <div className={styles.notesSection}>
                                <div className={styles.notesContainer}>
                                    <div className={styles.notesLabelContainer}>
                                        <span className={styles.notesLabel}>
                                            Consult call notes:
                                        </span>
                                    </div>
                                    <span className={styles.notesValue}>
                                        {appointmentCheck?.consult_call_notes}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className={styles.emailNotesSection}>
                        <div className={styles.editMode}>
                            <TextArea
                                label="Consent / CC Notes"
                                value={consentCcNotes}
                                onChange={(e) =>
                                    handleConsentCcNotesChange(e.target.value)
                                }
                            />
                        </div>
                    </div>
                    <div className={styles.emailNotesSection}>
                        {!editMode[appointment.id] && (
                            <div className={styles.notesSection}>
                                <div className={styles.notesContainer}>
                                    <div className={styles.notesLabelContainer}>
                                        <span className={styles.notesLabel}>
                                            Email notes:
                                        </span>
                                        <Button
                                            variant="normal"
                                            onClick={() =>
                                                handleEditClick(
                                                    appointment.id,
                                                    appointment.email_notes || ""
                                                )
                                            }
                                            className={styles.editButton}
                                            title="Edit email notes"
                                            aria-label="Edit email notes"
                                        >
                                            <EditIcon />
                                        </Button>
                                    </div>
                                    <span className={styles.notesValue}>
                                        {appointment.email_notes || (
                                            <span className={styles.notesEmpty}>
                                                None yet
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        )}

                        {editMode[appointment.id] && (
                            <div className={styles.editMode}>
                                <TextArea
                                    label="Email notes"
                                    value={editedNotes[appointment.id] || ""}
                                    onChange={(e) =>
                                        handleInputChange(
                                            appointment.id,
                                            e.target.value
                                        )
                                    }
                                    placeholder="Enter email notes"
                                />
                                <div className={styles.editActions}>
                                    <div>
                                        <Button
                                            onClick={() =>
                                                handleSaveClick(appointment)
                                            }
                                        >
                                            Save
                                        </Button>
                                    </div>
                                    <div>
                                        <Button
                                            variant="secondary"
                                            onClick={() =>
                                                handleCancelEdit(appointment.id)
                                            }
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {clinicianDetail.length > 0 && (
                <SelectIntakeSessionEvaluatorDialog
                    open={showIntakeSessionDialog}
                    onClose={() => {
                        setShowIntakeSessionDialog(false);
                        setIntakeActionReason("");
                    }}
                    supervisors={supervisors}
                    intakeAvailabilityBySupervisor={intakeAvailabilityBySupervisor}
                    selectedClinician={clinicianDetail[0]}
                    defaultSupervisorId={
                        appointment.supervisor_intake_session?.supervisor_id || ""
                    }
                    bookingPracticeId={appointment.practice_id}
                    allDepartmentsAndServices={allDepartmentsAndServices}
                    onSelect={handleIntakeSessionSelect}
                    showRescheduleReason={Boolean(
                        appointment.supervisor_intake_session
                    )}
                    rescheduleReason={intakeActionReason}
                    onRescheduleReasonChange={setIntakeActionReason}
                    viewFrom="appt_held"
                />
            )}
        </>
    );
}
